/**
 * Outreach enrollments — persistence for one-contact-through-one-sequence
 * records (`outreachEnrollments`).
 *
 * The sibling of src/lib/db/outreachSequences.ts. Sequences are the cadence
 * definition; enrollments are contacts walking through them. The PURE
 * due-step + analytics engine lives in src/lib/crm/sequenceRunner.ts; this
 * module is just the store wrapper so enrollments survive reload and sync
 * like everything else. Nothing here sends anything — the runner agent
 * proposes sends into the approval queue, and the executor advances an
 * enrollment on approval.
 *
 * Honest accounting: nothing is seeded — an empty list means no one is
 * enrolled. Mirrors outreachSequences.ts.
 */

import { db, nowIso, uuid } from './store';
import type { OutreachEnrollment } from './types';

/** Everything a caller provides to enroll a contact; id/timestamps derived. */
export interface EnrollmentInput {
  sequenceId: string;
  personId: string;
  personName?: string;
  /** Defaults to now — override only for deterministic tests / backfill. */
  enrolledAt?: string;
}

/**
 * Enroll a contact into a sequence at step 0, status active. Returns the
 * persisted record. Callers should guard against duplicate active
 * enrollments (see `isEnrolled`).
 */
export async function enrollContact(
  input: EnrollmentInput,
): Promise<OutreachEnrollment> {
  const ts = nowIso();
  const record: OutreachEnrollment = {
    id: uuid(),
    sequenceId: input.sequenceId,
    personId: input.personId,
    ...(input.personName !== undefined ? { personName: input.personName } : {}),
    enrolledAt: input.enrolledAt ?? ts,
    currentStep: 0,
    status: 'active',
    createdAt: ts,
    updatedAt: ts,
  };
  await db.add('outreachEnrollments', record);
  return record;
}

/** All enrollments, newest-first. */
export async function listEnrollments(): Promise<OutreachEnrollment[]> {
  const all = await db.list('outreachEnrollments');
  return [...all].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
}

/** Get one enrollment by id. */
export async function getEnrollment(
  id: string,
): Promise<OutreachEnrollment | null> {
  return db.get('outreachEnrollments', id);
}

/** Patch an enrollment; stamps `updatedAt`. */
export async function updateEnrollment(
  id: string,
  patch: Partial<Omit<OutreachEnrollment, 'id'>>,
): Promise<OutreachEnrollment> {
  return db.update('outreachEnrollments', id, { ...patch, updatedAt: nowIso() });
}

/** True when the contact already has an ACTIVE enrollment in the sequence. */
export async function isEnrolled(
  sequenceId: string,
  personId: string,
): Promise<boolean> {
  const all = await db.list('outreachEnrollments');
  return all.some(
    (e) =>
      e.sequenceId === sequenceId &&
      e.personId === personId &&
      e.status === 'active',
  );
}

/** Subscribe to enrollments, newest-first. */
export function subscribeEnrollments(
  fn: (enrollments: OutreachEnrollment[]) => void,
): () => void {
  return db.subscribe('outreachEnrollments', (rows) =>
    fn([...rows].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))),
  );
}
